import React, { useState } from "react";
import UserDetail from "./UserDetail";
import styles from "./styles.module.scss";

export const roleLabel = (user) => {
    if (!user.roles || user.roles.length === 0) return "-";
    return user.roles.join(", ");
};

export const isOperator = (user) => (user.roles || []).includes("operator");

// The assignable cpty-user roles. Mirrors the backend's whitelist
// (KeycloakClient.CPTY_ROLES); extending the platform with a new cpty
// role means adding it in both places.
export const CPTY_ROLES = ["trader", "auditor"];

// One user row, shared by the platform-users table and the per-cpty users page.
// Role switching (onChangeRole) is passed only for cpty users; disable/enable
// and delete are staged via pendingAction and applied through a confirm bar.
// Clicking the email expands an inline detail row (verification, 2FA, active
// sessions); History opens the user's activity timeline.
const UserRow = ({ user, onToggleEnabled, onDelete, onChangeRole, onResetPassword, onResetTotp, onShowHistory, stagedRole, pendingAction }) => {
    const locked = isOperator(user);
    const currentCptyRole = CPTY_ROLES.find((r) => (user.roles || []).includes(r)) || "";
    const shownRole = stagedRole || currentCptyRole;

    const [expanded, setExpanded] = useState(false);

    const markedDelete = pendingAction === "delete";
    const effectiveEnabled =
        pendingAction === "enable" ? true : pendingAction === "disable" ? false : user.enabled;
    const enabledPending = pendingAction === "enable" || pendingAction === "disable";

    // Status precedence: Disabled > Pending > Unverified > Enabled. A disabled
    // account can't sign in at all; a seeded operator/supervisor that hasn't set
    // a password + enrolled a passkey is Pending (exists but unusable); a
    // self-signup with an unverified email is Unverified; otherwise Enabled.
    const unverified = !user.emailVerified;
    const statusLabel = !effectiveEnabled
        ? "Disabled"
        : user.pending ? "Pending" : unverified ? "Unverified" : "Enabled";
    const statusClass = !effectiveEnabled
        ? styles.disabled
        : user.pending ? styles.pending : unverified ? styles.unverified : styles.enabled;

    return (
        <>
            <tr data-testid={`user-row-${user.email}`} className={markedDelete ? styles.rowMarkedDelete : ""}>
                <td>
                    <span className={styles.expandCell}>
                        <button
                            className={styles.expandCaret}
                            data-testid={`user-expand-${user.email}`}
                            aria-expanded={expanded}
                            aria-label={expanded ? "Collapse" : "Expand"}
                            onClick={() => setExpanded((v) => !v)}
                        >
                            {expanded ? "▾" : "▸"}
                        </button>
                        <span
                            className={styles.emailText}
                            onClick={() => {
                                // A click toggles, but if the operator is selecting the
                                // email to copy it, leave the selection intact.
                                const sel = window.getSelection && window.getSelection();
                                if (sel && !sel.isCollapsed) return;
                                setExpanded((v) => !v);
                            }}
                        >
                            {user.email}
                        </span>
                    </span>
                </td>
                <td>{[user.firstName, user.lastName].filter(Boolean).join(" ") || "-"}</td>
                <td>
                    {onChangeRole && !locked ? (
                        <select
                            className={`${styles.roleSelect} ${stagedRole ? styles.roleSelectPending : ""}`}
                            data-testid={`user-role-${user.email}`}
                            value={shownRole}
                            disabled={markedDelete}
                            onChange={(e) => onChangeRole(user, e.target.value)}
                        >
                            {shownRole === "" && <option value="">-</option>}
                            {CPTY_ROLES.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    ) : (
                        roleLabel(user)
                    )}
                </td>
                <td>
                    <span className={statusClass}>{statusLabel}</span>
                    {enabledPending && <span className={styles.pendingBadge}>pending</span>}
                    {markedDelete && <span className={styles.pendingBadge}>marked for deletion</span>}
                </td>
                <td>
                    <span className={user.twoFactor ? styles.enabled : styles.disabled}>
                        {user.twoFactor ? `Enabled (${user.twoFactor})` : "Disabled"}
                    </span>
                </td>
                <td className={styles.rowActions}>
                    <button
                        className={styles.actionLink}
                        data-testid={`user-history-${user.email}`}
                        onClick={() => onShowHistory && onShowHistory(user)}
                    >
                        History
                    </button>
                    {locked ? (
                        <span className={styles.protectedNote}>Admin (protected)</span>
                    ) : (
                        <>
                            {onResetPassword && (
                                <button
                                    className={styles.actionLink}
                                    data-testid={`user-reset-${user.email}`}
                                    onClick={() => onResetPassword(user)}
                                >
                                    Reset Password
                                </button>
                            )}
                            {/* Shown always so the action is discoverable, clickable only
                                against an enrolled TOTP. A passkey is a different
                                credential type and this would not clear it. */}
                            {onResetTotp && (
                                <button
                                    className={styles.actionLink}
                                    data-testid={`user-reset-totp-${user.email}`}
                                    disabled={user.twoFactor !== "TOTP"}
                                    title={
                                        user.twoFactor === "TOTP"
                                            ? "Clear this user's authenticator so they enrol a new one"
                                            : "No TOTP is enrolled for this user"
                                    }
                                    onClick={() => onResetTotp(user)}
                                >
                                    Reset TOTP
                                </button>
                            )}
                            <button
                                className={`${styles.actionLink} ${enabledPending ? styles.actionPending : ""}`}
                                data-testid={`user-toggle-${user.email}`}
                                disabled={markedDelete}
                                onClick={() => onToggleEnabled(user)}
                            >
                                {effectiveEnabled ? "Disable" : "Enable"}
                            </button>
                            <button
                                className={`${styles.deleteLink} ${markedDelete ? styles.actionPending : ""}`}
                                data-testid={`user-delete-${user.email}`}
                                onClick={() => onDelete(user)}
                            >
                                {markedDelete ? "Undo delete" : "Delete"}
                            </button>
                        </>
                    )}
                </td>
            </tr>
            {expanded && (
                <tr className={styles.detailRow} data-testid={`user-detail-${user.email}`}>
                    <td colSpan={6}>
                        <UserDetail user={user} />
                    </td>
                </tr>
            )}
        </>
    );
};

export default UserRow;
